import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Wallet, Plus, Pencil, Check, X, ChevronDown, ChevronRight, Trash2, AlertTriangle } from 'lucide-react';

const fmt = v => `\u20B9${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Wire / SWIFT', 'Cash', 'Adjustment'];

/**
 * Client revenue ledger — contracted project value (from the Client
 * Onboarding wizard) vs money actually received (punched manually here)
 * vs what's still pending. No demo numbers anywhere.
 */
export default function ProjectRevenuePanel({ onChange }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [receipts, setReceipts] = useState({});
  const [modalProject, setModalProject] = useState(null);
  const [err, setErr] = useState('');

  const load = () => api.get('/accounting/projects').then(r => setData(r.data)).catch(() => setData({ projects: [] }));
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { load(); onChange?.(); };

  const saveValue = async (p) => {
    try {
      await api.put(`/accounting/projects/${p.id}/value`, { value: Number(editVal || 0) });
      setEditing(null); setEditVal(''); refresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not save project value'); }
  };

  const toggleReceipts = async (p) => {
    if (expanded === p.id) { setExpanded(null); return; }
    setExpanded(p.id);
    if (!receipts[p.id]) {
      const r = await api.get(`/accounting/receipts?project_id=${p.id}`).catch(() => ({ data: { receipts: [] } }));
      setReceipts(prev => ({ ...prev, [p.id]: r.data.receipts || [] }));
    }
  };

  const deleteReceipt = async (pid, rid) => {
    if (!window.confirm('Remove this receipt from the books?')) return;
    await api.delete(`/accounting/receipts/${rid}`).catch(() => {});
    const r = await api.get(`/accounting/receipts?project_id=${pid}`).catch(() => ({ data: { receipts: [] } }));
    setReceipts(prev => ({ ...prev, [pid]: r.data.receipts || [] }));
    refresh();
  };

  const rows = data?.projects || [];
  const missingValue = useMemo(() => rows.filter(r => r.value <= 0 && r.status === 'active').length, [rows]);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid="project-revenue-panel">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-400" />
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Client Revenue Ledger</h3>
          <span className="text-[10px] text-zinc-600">project value from Client Onboarding · receipts punched here</span>
        </div>
        <div className="flex items-center gap-4 text-[10px]">
          <span className="text-zinc-500">Value <span className="text-violet-300 font-mono ml-1">{fmt(data?.total_value)}</span></span>
          <span className="text-zinc-500">Received <span className="text-emerald-400 font-mono ml-1">{fmt(data?.total_received)}</span></span>
          <span className="text-zinc-500">Pending <span className="text-amber-400 font-mono ml-1">{fmt(data?.total_pending)}</span></span>
        </div>
      </div>

      {err && <div className="px-4 py-2 bg-red-500/10 text-red-300 text-[11px]" data-testid="revenue-error">{err}</div>}

      {missingValue > 0 && (
        <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2" data-testid="missing-value-warning">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <p className="text-[11px] text-amber-200">
            {missingValue} active project{missingValue > 1 ? 's have' : ' has'} no contract value yet — type it in the Project Value column (writes the same field as Client Onboarding · Step 3 Budget).
          </p>
        </div>
      )}

      {!data ? (
        <p className="px-4 py-8 text-center text-xs text-zinc-600">Loading ledger…</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs text-zinc-600" data-testid="revenue-empty">No client projects yet.</p>
      ) : (
        <div className="divide-y divide-zinc-800/40">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-1.5 bg-zinc-950/50 text-[9px] uppercase text-zinc-600 tracking-wider">
            <span className="col-span-4">Project · Client</span>
            <span className="col-span-2 text-right">Project Value</span>
            <span className="col-span-2 text-right">Received</span>
            <span className="col-span-2 text-right">Pending</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {rows.map(p => (
            <div key={p.id} data-testid={`project-money-row-${p.id}`}>
              <div className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-zinc-900/40">
                <div className="col-span-12 md:col-span-4 min-w-0">
                  <button onClick={() => toggleReceipts(p)} data-testid={`expand-receipts-${p.id}`} className="flex items-center gap-1.5 text-left w-full group">
                    {expanded === p.id ? <ChevronDown className="w-3 h-3 text-zinc-500" /> : <ChevronRight className="w-3 h-3 text-zinc-600 group-hover:text-zinc-400" />}
                    <span className="text-xs text-white truncate">{p.name}</span>
                  </button>
                  <p className="text-[10px] text-zinc-600 pl-4.5 truncate">
                    {p.client_name || '—'} · {p.receipt_count} receipt{p.receipt_count === 1 ? '' : 's'}
                    {p.delivered_on ? <span className="text-emerald-400/80"> · Delivered {p.delivered_on}</span> : null}
                  </p>
                </div>
                <div className="col-span-4 md:col-span-2 text-right">
                  {editing === p.id ? (
                    <div className="flex items-center gap-1 justify-end">
                      <input autoFocus type="number" value={editVal} onChange={e => setEditVal(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveValue(p); if (e.key === 'Escape') setEditing(null); }}
                        data-testid={`value-input-${p.id}`}
                        className="w-24 bg-zinc-950 border border-violet-500/50 rounded-sm px-1.5 py-0.5 text-[11px] text-white font-mono text-right" />
                      <button onClick={() => saveValue(p)} data-testid={`save-value-${p.id}`} className="text-emerald-400 hover:text-emerald-300"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditing(p.id); setEditVal(String(p.value || '')); setErr(''); }} data-testid={`edit-value-${p.id}`}
                      className="text-[11px] font-mono text-violet-300 hover:text-violet-200 inline-flex items-center gap-1 group">
                      {p.value > 0 ? fmt(p.value) : <span className="text-zinc-600 italic">set value</span>}
                      <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
                    </button>
                  )}
                </div>
                <p className="col-span-4 md:col-span-2 text-right text-[11px] font-mono text-emerald-400">{fmt(p.received)}</p>
                <p className={`col-span-4 md:col-span-2 text-right text-[11px] font-mono ${p.pending > 0 ? 'text-amber-400' : 'text-zinc-600'}`}>{fmt(p.pending)}</p>
                <div className="col-span-12 md:col-span-2 flex md:justify-end">
                  <button onClick={() => setModalProject(p)} data-testid={`add-receipt-${p.id}`}
                    className="h-7 px-2.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-[10px] rounded-sm flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Receipt
                  </button>
                </div>
              </div>

              {expanded === p.id && (
                <div className="px-4 pb-3 bg-zinc-950/40" data-testid={`receipts-list-${p.id}`}>
                  {(receipts[p.id] || []).length === 0 ? (
                    <p className="text-[10px] text-zinc-600 py-2">No money received against this project yet.</p>
                  ) : (
                    <div className="divide-y divide-zinc-800/40">
                      {(receipts[p.id] || []).map(r => (
                        <div key={r.id} className="flex items-center justify-between py-1.5" data-testid={`receipt-row-${r.id}`}>
                          <div className="min-w-0">
                            <p className="text-[11px] text-zinc-300">{r.received_on} · <span className="text-zinc-500">{r.mode}</span> {r.reference ? <span className="font-mono text-zinc-600">#{r.reference}</span> : null}</p>
                            {(r.milestone || r.notes) && <p className="text-[10px] text-zinc-600 truncate">{r.milestone}{r.milestone && r.notes ? ' · ' : ''}{r.notes}</p>}
                          </div>
                          <div className="flex items-center gap-3">
                            {r.tds_by_client > 0 && <span className="text-[9px] text-rose-300">client TDS {fmt(r.tds_by_client)}</span>}
                            <span className="text-[11px] font-mono text-emerald-400">+{fmt(r.amount)}</span>
                            <button onClick={() => deleteReceipt(p.id, r.id)} data-testid={`delete-receipt-${r.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalProject && (
        <ReceiptModal project={modalProject} onClose={() => setModalProject(null)}
          onSaved={async () => {
            const pid = modalProject.id;
            setModalProject(null);
            const r = await api.get(`/accounting/receipts?project_id=${pid}`).catch(() => ({ data: { receipts: [] } }));
            setReceipts(prev => ({ ...prev, [pid]: r.data.receipts || [] }));
            refresh();
          }} />
      )}
    </div>
  );
}

function ReceiptModal({ project, onClose, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    amount: '', received_on: new Date().toISOString().slice(0, 10),
    mode: 'NEFT', reference: '', milestone: '', tds_by_client: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!(Number(form.amount) > 0)) { setError('Enter the amount received'); return; }
    setBusy(true); setError('');
    try {
      await api.post('/accounting/receipts', {
        project_id: project.id,
        amount: Number(form.amount),
        received_on: form.received_on,
        mode: form.mode,
        reference: form.reference,
        milestone: form.milestone,
        tds_by_client: Number(form.tds_by_client || 0),
        notes: form.notes,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save the receipt');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="receipt-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-md w-full max-w-lg">
        <header className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-heading font-bold text-white">Money Received</h3>
            <p className="text-[10px] text-zinc-500">{project.name} · {project.client_name || 'client'}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </header>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount received (₹)">
              <input type="number" value={form.amount} onChange={e => set('amount', e.target.value)} data-testid="receipt-amount-input"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white font-mono focus:border-emerald-500/60 outline-none" placeholder="0.00" />
            </Field>
            <Field label="Received on">
              <input type="date" value={form.received_on} onChange={e => set('received_on', e.target.value)} data-testid="receipt-date-input"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white focus:border-emerald-500/60 outline-none" />
            </Field>
            <Field label="Mode">
              <select value={form.mode} onChange={e => set('mode', e.target.value)} data-testid="receipt-mode-select"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white outline-none">
                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="UTR / Reference">
              <input value={form.reference} onChange={e => set('reference', e.target.value)} data-testid="receipt-ref-input"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white font-mono outline-none" placeholder="UTR / cheque no." />
            </Field>
            <Field label="Against milestone">
              <input value={form.milestone} onChange={e => set('milestone', e.target.value)} data-testid="receipt-milestone-input"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white outline-none" placeholder="Advance / Mid / Final" />
            </Field>
            <Field label="TDS deducted by client (₹)">
              <input type="number" value={form.tds_by_client} onChange={e => set('tds_by_client', e.target.value)} data-testid="receipt-client-tds-input"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white font-mono outline-none" placeholder="0.00" />
            </Field>
          </div>
          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} data-testid="receipt-notes-input"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-white outline-none resize-none" />
          </Field>
          {error && <p className="text-[11px] text-red-400" data-testid="receipt-error">{error}</p>}
        </div>
        <footer className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 text-xs text-zinc-400 hover:text-white">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="receipt-save-btn"
            className="h-9 px-5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-zinc-950 text-xs font-bold rounded-sm">
            {busy ? 'Saving…' : 'Record Receipt'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] uppercase text-zinc-500 tracking-wider">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
